import { Component } from "@theme/component";
import { fetchConfig, debounce, resetLoading } from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #timeout = 5000;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener("change", this.#onSellingPlanChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener("change", this.#onSellingPlanChange);
  }

  #onSellingPlanChange = async (event) => {
    const select = event.target.closest("[data-cart-selling-plan]");
    if (!(select instanceof HTMLSelectElement)) return;

    const row = select.closest("[data-key]");
    if (!row) return;

    const previousPlanId = select.dataset.selectedPlanId || select.querySelector("option[selected]")?.value;
    const sellingPlanId = select.value;
    if (!sellingPlanId || sellingPlanId === previousPlanId) return;

    const errorMessage = row.querySelector("[data-cart-plan-error]");
    const control = select.closest(".cart-items__selling-plan-control");
    const spinner = control?.querySelector(".cart-items__selling-plan-spinner");
    if (errorMessage) errorMessage.textContent = "";
    select.disabled = true;
    control?.classList.add("is-loading");
    control?.setAttribute("aria-busy", "true");
    spinner?.classList.remove("hidden");
    this.#disableCartItems();

    try {
      const lineProperties = JSON.parse(row.dataset.lineProperties || "{}");
      const properties = Array.isArray(lineProperties)
        ? Object.fromEntries(lineProperties.map((property) => {
          if (Array.isArray(property)) return property;
          return [property.first, property.last];
        }))
        : lineProperties;
      const previousMonths = Number(select.querySelector(`option[value="${previousPlanId}"]`)?.dataset.planMonths) || 1;
      const newMonths = Number(select.selectedOptions[0]?.dataset.planMonths) || 1;
      const newQuantity = this.#convertQuantityToPlan(Number(row.dataset.lineQuantity), previousMonths, newMonths);

      const addResponse = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", {
        body: JSON.stringify({
          items: [{
            id: row.dataset.variantId,
            quantity: newQuantity,
            selling_plan: sellingPlanId,
            properties,
          }],
        }),
      })).then((response) => response.json());


        const cart = await fetch(FoxTheme.routes.cart).then((response) => response.json());
        const previousLine = cart.items.findIndex((item) => item.key === row.dataset.key);
        if (previousLine === -1) throw new Error("The original cart item could not be found.");
      if (addResponse.status) throw new Error(addResponse.description || addResponse.message);

      const sectionsToUpdate = new Set();
      document.querySelectorAll("cart-items-component").forEach((component) => {
        if (component.dataset.sectionId) sectionsToUpdate.add(component.dataset.sectionId);
      });

      const changeResponse = await fetch(FoxTheme.routes.cart_change_url, fetchConfig("json", {
        body: JSON.stringify({
          line: previousLine + 1,
          quantity: 0,
          sections: Array.from(sectionsToUpdate).join(","),
          sections_url: window.location.pathname,
        }),
      })).then((response) => response.json());

      if (changeResponse.errors || changeResponse.status) {
        throw new Error(changeResponse.errors || changeResponse.description || changeResponse.message);
      }

      document.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
        itemCount: changeResponse.item_count,
        source: "cart-items-component",
        sections: changeResponse.sections,
      }));
    } catch (error) {
      select.value = previousPlanId || "";
      select.setAttribute("aria-invalid", "true");
      if (errorMessage) errorMessage.textContent = error.message || "Unable to update subscription plan.";
      console.error("Failed to update subscription plan:", error);
    } finally {
      select.disabled = false;
      control?.classList.remove("is-loading");
      control?.removeAttribute("aria-busy");
      spinner?.classList.add("hidden");
      this.#enableCartItems();
    }
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { cartLine: line } = event.detail;
    let { quantity } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];

    // Abonnementen gaan in staffels van het aantal maanden, dus getypte waarden worden hier afgerond
    const planMonths = Number(lineItemRow?.querySelector("[data-cart-selling-plan] option:checked")?.dataset.planMonths) || 0;
    if (planMonths > 1) {
      quantity = this.#snapToPlanMonths(quantity, planMonths);
      const quantityInput = lineItemRow?.querySelector("input.quantity-input");
      if (quantityInput) quantityInput.value = quantity;
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });

    if (!lineItemRow) return;

    const removeButtons = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelectorAll(".cart-items__remove-button")
    );
    removeButtons?.forEach((button) => {
      button?.classList.add("btn--loading");
    });
  }

  /**
   * Rondt een aantal af naar een veelvoud van het aantal maanden van het plan (minimaal één keer de maanden).
   * @param {number} quantity - The quantity.
   * @param {number} months - The number of months of the selling plan.
   * @returns {number}
   */
  #snapToPlanMonths(quantity, months) {
    return Math.max(months, Math.round(quantity / months) * months);
  }

  /**
   * Behoudt het aantal per maand bij een wissel van plan (18 voor 6 maanden wordt 9 voor 3 maanden).
   * @param {number} quantity - The current quantity.
   * @param {number} fromMonths - The months of the current plan.
   * @param {number} toMonths - The months of the new plan.
   * @returns {number}
   */
  #convertQuantityToPlan(quantity, fromMonths, toMonths) {
    const perMonth = quantity / fromMonths;
    return Math.max(toMonths, Math.round(perMonth) * toMonths);
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, event) {
    event?.preventDefault();

    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const removeButtons = /** @type {TextComponent | undefined} */ (
      cartItemRowToRemove.querySelectorAll(".cart-items__remove-button")
    );
    removeButtons.forEach((button) => {
      button?.classList.add("btn--loading");
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.#disableCartItems();

    const { line, quantity } = config;

    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    fetch(`${FoxTheme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetLoading(this);

        // Even with errors, backend may have updated cart to max available
        // Update UI and cart count if we have sections
        if (parsedResponseText.sections && parsedResponseText.sections[this.sectionId]) {
          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            "text/html"
          );

          // Grab the new cart item count from a hidden element
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: "cart-items-component",
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        } else if (parsedResponseText.errors) {
          // No sections in error response - fetch cart.js for accurate count and quantity

          const cartSectionsData = {};
          let cartJson = null;
          const cartSectionsPromises = Array.from(sectionsToUpdate).map(async (sectionId) => {
            const sectionUrl = `${window.location.pathname.split("?")[0]}?section_id=${sectionId}`;

            const res = await fetch(sectionUrl);
            const html = await res.text();

            cartSectionsData[sectionId] = html;
          });

          const cartJsonPromises = fetch(FoxTheme.routes.cart)
            .then((res) => res.json())
            .then((data) => {
              cartJson = data;
            });

          await Promise.all([...cartSectionsPromises, cartJsonPromises]);

          cartJson["sections"] = cartSectionsData;

          this.dispatchEvent(
            new CartUpdateEvent(cartJson, "", {
              itemCount: cartJson.item_count || 0,
              sections: cartJson.sections,
            })
          );

          morphSection(this.sectionId, cartJson.sections[this.sectionId]);
        }

        /**
         * Show error message if exists (e.g. quantity exceeds available)
         * Call after morph section to avoid message disappear
         */
        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        // cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    if (event?.detail?.sourceId === this.sectionId) return;
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");

    setTimeout(() => {
      cartItemErrorContainer.classList.add("hidden");
    }, this.#timeout);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (event?.detail?.sourceId === this.sectionId) return;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
